
import { Vue, Prop, Component } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import BarcodeOrderModule from "@/store/modules/barcodeSystem/barcodeOrder";
import { BarcodeNoScanLineItemListItem } from "@/models/api/barcodeSystem/barcodeOrders/get";
import { formatDateWithTime, formatBool } from "@/utils";
import { UpdateBarcodeNoScanLineItemRequest } from "@/models/api/barcodeSystem/barcodeOrders/updateBarcodeNoScanLineItem";
import { CreateBarcodeNoScanLineItemRequest } from "@/models/api/barcodeSystem/barcodeOrders/createBarcodeNoScanLineItem";
import { DeleteBarcodeNoScanLineItemRequest } from "@/models/api/barcodeSystem/barcodeOrders/deleteBarcodeNoScanLineItem";
import notifications from "@/lib/notifications";
import BarcodeNoScanLineItemUpdate from "@/components/barcodeSystem/barcodeOrder/BarcodeNoScanLineItemUpdate.vue"
import permissions from "@/security/permissions";
import draggable from "vuedraggable";
import RightClickMenu from "@/components/widgets/RightClickMenu.vue";
import BarcodeNoScanLineItemCreate from "./BarcodeNoScanLineItemCreate.vue";
import BarcodeNoScanLineItemDelete from "./BarcodeNoScanLineItemDelete.vue";

const barcodeOrderModuleState = getModule(BarcodeOrderModule);

@Component({
  components: {
    BarcodeNoScanLineItemUpdate,
    BarcodeNoScanLineItemCreate,
    BarcodeNoScanLineItemDelete,
    RightClickMenu,
    draggable
  }
})
export default class BarcodeNoScanLineItemTable extends Vue {
  @Prop({ default: false })
  loading: boolean;

  @Prop({ default: false })
  orderId: number;

  @Prop({ default: null })
  tableMenuItem: Array<any>; 

  rightClickLocation: any = null;
  draggingData: boolean = false;
  showUpdateNoScanLineItemModal: boolean = false;
  showCreateNoScanLineItemModal: boolean = false;
  showDeleteNoScanLineItemModal: boolean = false;
  updateItemLoading: boolean = false;
  updateBarcodeNoScanLineItemRequest: UpdateBarcodeNoScanLineItemRequest = null;
  updatingItemValidationMessages: string[] = null;
  createBarcodeNoScanLineItemRequest: CreateBarcodeNoScanLineItemRequest = null;
  creatingItemValidationMessages: string[] = null;
  deleteBarcodeNoScanLineItemRequest: DeleteBarcodeNoScanLineItemRequest = null;

  get isAgentObject() : boolean
  {
    return barcodeOrderModuleState.currentBarcodeOrder ? barcodeOrderModuleState.currentBarcodeOrder.isAgentObject : false;
  }

  get allowManualNoScanItems() : boolean
  {
    return barcodeOrderModuleState.currentBarcodeOrder ? barcodeOrderModuleState.currentBarcodeOrder.allowManualNoScanItems : false;
  }

  get barcodeNoScanLineItems(): Array<BarcodeNoScanLineItemListItem> {
    return barcodeOrderModuleState.currentBarcodeNoScanLineItems;
  }

  get selectedItemCount(): number {
    return barcodeOrderModuleState.getSelectedBarcodeNoScanLineItemCount;
  }

  get selectedItems(): Array<any> {
    return barcodeOrderModuleState.getSelectedBarcodeNoScanLineItems;
  }

  get totalItems() {
    if (barcodeOrderModuleState.currentBarcodeNoScanLineItems) {
      return barcodeOrderModuleState.currentBarcodeNoScanLineItems.length
    } else {
      return 0;
    }
  }
  
  get isUpdateable() : boolean
  {
    return this.updateBarcodeNoScanLineItemRequest
      ? this.updateBarcodeNoScanLineItemRequest.isUpdateable 
      : false;
  }

  get isAddable() : boolean
  {
    return this.createBarcodeNoScanLineItemRequest
      ? this.createBarcodeNoScanLineItemRequest.isAddable 
      : false;
  }

  get isRemoveable() : boolean
  {
    return this.deleteBarcodeNoScanLineItemRequest
      ? this.deleteBarcodeNoScanLineItemRequest.isUpdateable 
      : false;
  }

  async updateItemPrompt(data)
  {
    if(this.hasPermission(permissions.barcodeSystem.orders.noScanLineItems.save, this.isAgentObject))
    {
      this.updateItemLoading = true;
      this.updateBarcodeNoScanLineItemRequest = null;
      this.showUpdateNoScanLineItemModal=true;
      this.updateBarcodeNoScanLineItemRequest = await barcodeOrderModuleState.previewUpdateBarcodeNoScanLineItem(data);
      this.updateItemLoading = false;
    }
  }

  async createItemPrompt()
  {
    if(this.hasPermission(permissions.barcodeSystem.orders.noScanLineItems.save, this.isAgentObject))
    {
      this.updateItemLoading = true;
      this.createBarcodeNoScanLineItemRequest = null;
      this.showCreateNoScanLineItemModal=true;
      this.createBarcodeNoScanLineItemRequest = await barcodeOrderModuleState.previewCreateBarcodeNoScanLineItem(this.orderId);
      this.updateItemLoading = false;
    }
  }
  
  async deleteItemPrompt(data)
  {
    if(this.hasPermission(permissions.barcodeSystem.orders.noScanLineItems.save, this.isAgentObject))
    {
      this.updateItemLoading = true;
      this.deleteBarcodeNoScanLineItemRequest = null;
      this.showDeleteNoScanLineItemModal=true;
      this.deleteBarcodeNoScanLineItemRequest = await barcodeOrderModuleState.previewDeleteBarcodeNoScanLineItem(data);
      this.updateItemLoading = false;
    }
  }

  async updateNoScanLineItem() {
    if(this.validateNoScanLineItemUpdate())
    {
      this.updateItemLoading = true;
      try {
        await barcodeOrderModuleState.updateBarcodeNoScanLineItem(
          this.updateBarcodeNoScanLineItemRequest
        );
        notifications.success("Barcode No Scan Line Item Updated", "Barcode no scan line item successfully updated.");
        this.$emit("reload");
        this.closeModals();
      } catch (ex) {
        notifications.error("Updating Barcode No Scan Line Item", "Failed to update barcode no scan line item.", ex);
      } finally {
        this.updateItemLoading = false;
      }
    }
  }

  async createNoScanLineItem() {
    if(this.validateNoScanLineItemCreate())
    {
      this.updateItemLoading = true;
      try {
        await barcodeOrderModuleState.createBarcodeNoScanLineItem(
          this.createBarcodeNoScanLineItemRequest
        );
        notifications.success("Barcode No Scan Line Item Added", "Barcode no scan line item successfully added.");
        this.$emit("reload");
        this.closeModals();
      } catch (ex) {
        notifications.error("Adding Barcode No Scan Line Item", "Failed to add barcode no scan line item.", ex);
      } finally {
        this.updateItemLoading = false;
      }
    }
  }

  async deleteNoScanLineItem() {
      this.updateItemLoading = true;
      try {
        await barcodeOrderModuleState.deleteBarcodeNoScanLineItem(
          this.deleteBarcodeNoScanLineItemRequest
        );
        notifications.success("Barcode No Scan Line Item Removed", "Barcode no scan line item successfully removed.");
        this.$emit("reload");
        this.closeModals();
      } catch (ex) {
        notifications.error("Removing Barcode No Scan Line Item", "Failed to remove barcode no scan line item.", ex);
      } finally {
        this.updateItemLoading = false;
      }
    }

  validateNoScanLineItemUpdate() : boolean {
    let valid = this.updateBarcodeNoScanLineItemRequest != null;
    this.updatingItemValidationMessages = null;

    if(!valid)
      return false;

    if(this.updateBarcodeNoScanLineItemRequest.damaged && this.updateBarcodeNoScanLineItemRequest.quantityDamaged <= 0) {
      valid = false;
      if(!this.updatingItemValidationMessages)
        this.updatingItemValidationMessages = [];
      this.updatingItemValidationMessages.push('All damaged items must have a quantity damaged greater than 0.'); 
    }
    if(this.updateBarcodeNoScanLineItemRequest.quantityDamaged > this.updateBarcodeNoScanLineItemRequest.quantity) {
      valid = false;
      if(!this.updatingItemValidationMessages)
        this.updatingItemValidationMessages = [];
      this.updatingItemValidationMessages.push('Quantity daamged cannot be greater than an items quantity.'); 
    }
    if(this.updateBarcodeNoScanLineItemRequest.quantityDelivered > this.updateBarcodeNoScanLineItemRequest.quantity) {
      valid = false;
      if(!this.updatingItemValidationMessages)
        this.updatingItemValidationMessages = [];
      this.updatingItemValidationMessages.push('Quantity delivered cannot be greater than an items quantity.');
    }
    return valid;
  }

  validateNoScanLineItemCreate() : boolean {
    let valid = this.createBarcodeNoScanLineItemRequest != null;
    this.updatingItemValidationMessages = null;

    if(!valid)
      return false;

    if(this.createBarcodeNoScanLineItemRequest.damaged && this.createBarcodeNoScanLineItemRequest.quantityDamaged <= 0) {
      valid = false;
      if(!this.creatingItemValidationMessages)
        this.updatingItemValidationMessages = [];
      this.creatingItemValidationMessages.push('All damaged items must have a quantity damaged greater than 0.'); 
    }
    if(this.createBarcodeNoScanLineItemRequest.quantityDamaged > this.createBarcodeNoScanLineItemRequest.quantity) {
      valid = false;
      if(!this.creatingItemValidationMessages)
        this.creatingItemValidationMessages = [];
      this.creatingItemValidationMessages.push('Quantity daamged cannot be greater than an items quantity.'); 
    }
    if(this.createBarcodeNoScanLineItemRequest.quantityDelivered > this.createBarcodeNoScanLineItemRequest.quantity) {
      valid = false;
      if(!this.creatingItemValidationMessages)
        this.creatingItemValidationMessages = [];
      this.creatingItemValidationMessages.push('Quantity delivered cannot be greater than an items quantity.');
    }
    return valid;
  }

  getDamagedMessage(noScanLineItem) : string {
    let damageMessage = formatBool(noScanLineItem.damaged);
    if(noScanLineItem && noScanLineItem.damaged) {
      damageMessage = damageMessage + '(' + noScanLineItem.quantityDamaged + ')';
    }
    return damageMessage;
  }

  formatDateWithTime(val)
  {
    return formatDateWithTime(val)
  }

  formatBool(val)
  {
    return formatBool(val);
  }

  getComponentData(rowData) {
    return {
      on: {
        click: () => this.onRowClick(rowData),
        contextmenu: $event => this.onRightClicked(rowData, $event),
        dblclick: () => this.onDoubleClicked(rowData)
      }
    };
  }

  onRightClicked(rowData, event) {
    event.preventDefault();
    if (!rowData.selected) {
      this.unselectAll();
      barcodeOrderModuleState.switchBarcodeNoScanLineItemSelectionStatus(rowData.id);
    }
    this.rightClickLocation = { x: event.x, y: event.y };
  }

  clearRightClick() {
    this.rightClickLocation = null;
    this.draggingData = false;
  }

  onDoubleClicked(data) {
    this.updateItemPrompt(data);
  }

  onRowClick(data) {
    this.unselectAll();
    barcodeOrderModuleState.switchBarcodeNoScanLineItemSelectionStatus(data.id);
  }

  clickMenu(item) {
    switch(item.type)
    {
      case "updateItem":
        if(this.selectedItems != null && this.selectedItems.length == 1) {       
          this.updateItemPrompt(this.selectedItems[0]);
        }
        break;
      case "deleteItem":
        if(this.selectedItems != null && this.selectedItems.length == 1) {       
          this.deleteItemPrompt(this.selectedItems[0]);
        }
        break;
    }
    this.clearRightClick();
  }

  unselectAll() {
    let items = this.selectedItems;

    items.forEach(item => {
      barcodeOrderModuleState.switchBarcodeNoScanLineItemSelectionStatus(item.id);
    });
  }

  closeModals()
  {
    this.showUpdateNoScanLineItemModal=false;
    this.showCreateNoScanLineItemModal=false;
    this.showDeleteNoScanLineItemModal=false;
  }
}
